import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Users, UserPlus, Upload, Key, Search, Edit2, Trash2, RotateCcw,
  Loader2, Shield, User, Copy, Check, Eye, EyeOff, AlertTriangle, X, Globe2, Mail, Send
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listUsers, createUser, updateUser, deleteUser, restoreUser,
  importUsersCSV, importUsersFromATS, getUserCounts, listAPITokens, createAPIToken, revokeAPIToken,
  sendSetupEmails
} from '../services/api';

const REGION_OPTIONS = [
  { value: 'NA', label: 'North America' },
  { value: 'UK_EMEA', label: 'UK & EMEA' },
  { value: 'APAC', label: 'APAC' },
];

// ===== Create/Edit User Modal =====
const UserModal = ({ isOpen, onClose, user, onSaved }) => {
  const isEdit = !!user;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('recruiter');
  const [userType, setUserType] = useState('regular');
  const [regions, setRegions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setRole(user.role || 'recruiter');
      setUserType(user.user_type || 'regular');
      setRegions(user.regions || []);
      setPassword('');
    } else {
      setName(''); setEmail(''); setPassword(''); setRole('recruiter'); setUserType('regular');
      setRegions(['NA', 'UK_EMEA', 'APAC']);
    }
  }, [user, isOpen]);

  const toggleRegion = (r) => {
    setRegions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const data = { name, email, role, regions };
        if (password.trim()) data.password = password;
        await updateUser(user.id, data);
        toast.success('User updated');
      } else {
        await createUser({ name, email, password, role, user_type: userType, regions });
        toast.success('User created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="user-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{isEdit ? 'Edit User' : 'Create User'}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm">Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} required data-testid="user-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="user-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Password {isEdit && <span className="text-muted-foreground">(leave blank to keep current)</span>}</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
              required={!isEdit} minLength={6} data-testid="user-password-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Role</Label>
              <select value={role} onChange={e => setRole(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm" data-testid="user-role-select">
                <option value="admin">Admin</option>
                <option value="recruiter">Recruiter</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            {!isEdit && (
              <div className="space-y-1.5">
                <Label className="text-sm">Type</Label>
                <select value={userType} onChange={e => setUserType(e.target.value)}
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm" data-testid="user-type-select">
                  <option value="regular">Regular</option>
                  <option value="api">API User</option>
                </select>
              </div>
            )}
          </div>
          {/* Region Assignment */}
          <div className="space-y-1.5">
            <Label className="text-sm">Region Access</Label>
            <div className="flex gap-2 flex-wrap">
              {REGION_OPTIONS.map(({ value, label }) => (
                <button key={value} type="button"
                  className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                    regions.includes(value)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/30 text-muted-foreground border-border'
                  }`}
                  onClick={() => toggleRegion(value)}
                  data-testid={`region-toggle-${value}`}>
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">Admins have access to all regions regardless of selection</p>
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="user-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Update User' : 'Create User'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// ===== Import CSV Modal =====
const ImportModal = ({ isOpen, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;
  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const data = await importUsersCSV(file);
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} users imported`);
        onImported();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="import-modal">
        <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Import Users from CSV</h2>
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-muted/30 border">
            <p className="text-xs text-muted-foreground mb-2">CSV columns: <strong>name, email, password, role, user_type</strong></p>
            <p className="text-[10px] text-muted-foreground">Role: admin/user. Type: regular/api. Password auto-generated if blank.</p>
          </div>
          <Input type="file" accept=".csv" onChange={e => { setFile(e.target.files?.[0]); setResult(null); }} data-testid="import-file-input" />
          {result && (
            <div className="p-3 rounded-lg border bg-muted/10 text-sm space-y-1">
              <p className="text-green-600">Created: {result.created}</p>
              <p className="text-amber-600">Skipped: {result.skipped}</p>
              {result.errors?.length > 0 && (
                <div className="max-h-24 overflow-y-auto text-xs text-red-500 mt-1">
                  {result.errors.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1" onClick={handleImport} disabled={!file || importing} data-testid="import-submit-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Import
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== API Token Modal =====
const APITokenModal = ({ isOpen, onClose, users, onCreated }) => {
  const [userId, setUserId] = useState('');
  const [tokenName, setTokenName] = useState('');
  const [expDays, setExpDays] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const allPerms = ['jobs', 'candidates', 'interviews', 'analytics', 'users'];

  const togglePerm = (p) => {
    setPermissions(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  if (!isOpen) return null;
  const handleCreate = async () => {
    if (!userId || !tokenName.trim()) { toast.error('Select user and enter token name'); return; }
    setCreating(true);
    try {
      const data = await createAPIToken({
        user_id: userId,
        name: tokenName,
        permissions: permissions.length > 0 ? permissions : allPerms,
        expires_in_days: expDays ? parseInt(expDays) : null,
      });
      setNewToken(data.token);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create token');
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="api-token-modal">
        <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Create API Token</h2>
        {newToken ? (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs font-medium text-amber-600 mb-2">Copy this token now — it will not be shown again!</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-background p-2 rounded border flex-1 break-all">{newToken}</code>
                <Button size="sm" onClick={() => { navigator.clipboard.writeText(newToken); setCopied(true); toast.success('Token copied!'); }}
                  data-testid="copy-token-btn">
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
            <Button className="w-full" onClick={onClose}>Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">User</Label>
              <select value={userId} onChange={e => setUserId(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm" data-testid="token-user-select">
                <option value="">Select user...</option>
                {users.filter(u => u.status === 'active').map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Token Name</Label>
              <Input value={tokenName} onChange={e => setTokenName(e.target.value)} placeholder="e.g., ATS Integration" data-testid="token-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Expires in (days) <span className="text-muted-foreground">— blank = never</span></Label>
              <Input type="number" value={expDays} onChange={e => setExpDays(e.target.value)} placeholder="90" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Permissions</Label>
              <div className="flex flex-wrap gap-2">
                {allPerms.map(p => (
                  <button key={p} type="button"
                    className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                      permissions.includes(p) ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/30 text-muted-foreground'
                    }`}
                    onClick={() => togglePerm(p)}
                    data-testid={`perm-${p}`}>
                    {p}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground">Leave empty = full access</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button className="flex-1" onClick={handleCreate} disabled={creating} data-testid="create-token-btn">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
                Generate Token
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ROLE_COLORS = {
  admin: 'bg-primary text-primary-foreground',
  recruiter: 'bg-blue-500/10 text-blue-600',
  viewer: 'bg-slate-500/10 text-slate-500',
};

const REGION_SHORT = { NA: 'NA', UK_EMEA: 'UK', APAC: 'APAC' };

// ===== User Row =====
const UserRow = ({ user, onEdit, onDelete, onRestore, onToggleStatus, selected, onToggleSelect }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/20 transition-colors" data-testid={`user-row-${user.id}`}>
    <div className="flex items-center gap-3 min-w-0">
      {onToggleSelect && user.status === 'active' && (
        <input type="checkbox" checked={selected} onChange={() => onToggleSelect(user.id)}
          className="h-4 w-4 rounded border-slate-300 accent-indigo-500 shrink-0 cursor-pointer"
          data-testid={`select-user-${user.id}`} />
      )}
      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
        user.user_type === 'api' ? 'bg-orange-500/10' : user.role === 'admin' ? 'bg-primary/10' : 'bg-muted'
      }`}>
        {user.user_type === 'api' ? <Key className="h-4 w-4 text-orange-500" /> :
          user.role === 'admin' ? <Shield className="h-4 w-4 text-primary" /> :
          user.role === 'viewer' ? <Eye className="h-4 w-4 text-muted-foreground" /> :
            <User className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate">{user.name}</p>
          {user.ats_source && !user.password_set_at && !user.setup_email_sent && (
            <Badge variant="outline" className="text-[9px] px-1.5 text-amber-500 border-amber-500/30">No password</Badge>
          )}
          {user.setup_email_sent && !user.password_set_at && (
            <Badge variant="outline" className="text-[9px] px-1.5 text-blue-500 border-blue-500/30">Email sent</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
      </div>
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      <Badge className={`text-[10px] ${ROLE_COLORS[user.role] || ROLE_COLORS.recruiter}`}>{user.role}</Badge>
      {user.user_type === 'api' && <Badge className="bg-orange-500/10 text-orange-600 text-[10px]">API</Badge>}
      {user.regions?.length > 0 && user.role !== 'admin' && (
        <div className="flex gap-0.5">
          {user.regions.map(r => (
            <Badge key={r} variant="outline" className="text-[9px] px-1.5">{REGION_SHORT[r] || r}</Badge>
          ))}
        </div>
      )}
      <Badge className={`text-[10px] ${
        user.status === 'active' ? 'bg-green-500/10 text-green-600' :
        user.status === 'inactive' ? 'bg-amber-500/10 text-amber-600' :
        'bg-red-500/10 text-red-600'}`}>{user.status}</Badge>
      {user.status === 'deleted' ? (
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onRestore(user)} data-testid={`restore-user-${user.id}`}>
          <RotateCcw className="h-3 w-3 mr-1" /> Restore
        </Button>
      ) : (
        <>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => onEdit(user)} data-testid={`edit-user-${user.id}`}>
            <Edit2 className="h-3 w-3" />
          </Button>
          {user.status === 'active' && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-amber-500" onClick={() => onToggleStatus(user, 'inactive')}>
              <EyeOff className="h-3 w-3" />
            </Button>
          )}
          {user.status === 'inactive' && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-green-500" onClick={() => onToggleStatus(user, 'active')}>
              <Eye className="h-3 w-3" />
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500" onClick={() => onDelete(user)} data-testid={`delete-user-${user.id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </>
      )}
    </div>
  </div>
);

// ===== Token Row =====
const TokenRow = ({ token, onRevoke }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border" data-testid={`token-row-${token.token_id}`}>
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <Key className="h-3.5 w-3.5 text-orange-500" />
        <span className="text-sm font-medium">{token.name}</span>
        <Badge variant="secondary" className="text-[10px]">{token.token_preview}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">
        {token.user_name} · {token.permissions?.length > 0 ? token.permissions.join(', ') : 'Full access'}
        {token.last_used && ` · Last used: ${new Date(token.last_used).toLocaleDateString()}`}
      </p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <Badge className={`text-[10px] ${token.status === 'active' ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>{token.status}</Badge>
      {token.status === 'active' && (
        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" onClick={() => onRevoke(token)}
          data-testid={`revoke-token-${token.token_id}`}>
          Revoke
        </Button>
      )}
    </div>
  </div>
);


// ===== ATS User Import Modal =====
const ATSUserImportModal = ({ isOpen, onClose, onImported }) => {
  const [region, setRegion] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const handleImport = async () => {
    if (!region) { toast.error('Select a region'); return; }
    setImporting(true);
    try {
      const data = await importUsersFromATS(region);
      setResult(data);
      if (data.created > 0 || data.updated > 0) {
        toast.success(data.message);
        onImported();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="ats-user-import-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Import Users from ATS</h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-muted/30 border">
            <p className="text-xs text-muted-foreground">Pulls all users from Ceipal ATS for the selected region. Users are created as <strong>Recruiter</strong> role with access to the imported region.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">ATS Region</Label>
            <select value={region} onChange={e => { setRegion(e.target.value); setResult(null); }}
              className="w-full h-9 rounded-md border bg-background px-3 text-sm" data-testid="ats-import-region-select">
              <option value="">Select region...</option>
              {REGION_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {result && (
            <div className="p-3 rounded-lg border bg-muted/10 text-sm space-y-1">
              <p className="text-muted-foreground">Total ATS users found: <strong>{result.total_ats_users}</strong></p>
              <p className="text-green-600">New users created: <strong>{result.created}</strong></p>
              <p className="text-blue-600">Existing users — region access added: <strong>{result.updated}</strong></p>
              <p className="text-amber-600">Already had this region: <strong>{result.skipped}</strong></p>
              {result.updated > 0 && (
                <p className="text-xs text-muted-foreground mt-2 pt-2 border-t">Users found in multiple ATS regions are merged into one account with access to all their regions.</p>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1 gap-1.5" onClick={handleImport} disabled={!region || importing} data-testid="ats-import-submit-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe2 className="h-4 w-4" />}
              {importing ? 'Importing...' : 'Import from ATS'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ===== MAIN PAGE =====
export default function UsersPage() {
  const [tab, setTab] = useState('active');
  const [users, setUsers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showATSImportModal, setShowATSImportModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => {
    loadData();
  }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, countsData] = await Promise.all([
        listUsers(tab === 'api' ? 'all' : tab, tab === 'api' ? 'api' : 'all'),
        getUserCounts(),
      ]);
      setUsers(usersData);
      setCounts(countsData);
      if (tab === 'api') {
        const tokensData = await listAPITokens();
        setTokens(tokensData);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally { setLoading(false); }
  };

  const handleEdit = (user) => { setEditingUser(user); setShowUserModal(true); };
  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.name}? They will be moved to Deleted.`)) return;
    try { await deleteUser(user.id); toast.success('User deleted'); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };
  const handleRestore = async (user) => {
    try { await restoreUser(user.id); toast.success('User restored'); loadData(); }
    catch (err) { toast.error('Failed to restore'); }
  };
  const handleToggleStatus = async (user, newStatus) => {
    try { await updateUser(user.id, { status: newStatus }); toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`); loadData(); }
    catch (err) { toast.error('Failed to update status'); }
  };
  const handleRevokeToken = async (token) => {
    if (!window.confirm(`Revoke token "${token.name}"?`)) return;
    try { await revokeAPIToken(token.token_id); toast.success('Token revoked'); loadData(); }
    catch (err) { toast.error('Failed to revoke token'); }
  };

  const filtered = users.filter(u => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s);
  });

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const activeIds = filtered.filter(u => u.status === 'active').map(u => u.id);
    if (selectedIds.size === activeIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(activeIds));
    }
  };

  const handleSendSetupEmails = async () => {
    if (selectedIds.size === 0) { toast.error('Select users first'); return; }
    if (!window.confirm(`Send password setup emails to ${selectedIds.size} user(s)?`)) return;
    setSendingEmails(true);
    try {
      const result = await sendSetupEmails([...selectedIds]);
      if (result.sent > 0) toast.success(result.message);
      if (result.failed > 0) toast.error(`${result.failed} emails failed`);
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send emails');
    } finally { setSendingEmails(false); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Users</h1>
          <p className="text-sm text-muted-foreground">Manage team members and API access</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowATSImportModal(true)} data-testid="import-ats-btn">
            <Globe2 className="h-3.5 w-3.5" /> Import from ATS
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowImportModal(true)} data-testid="import-csv-btn">
            <Upload className="h-3.5 w-3.5" /> Import CSV
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowTokenModal(true)} data-testid="create-token-btn">
            <Key className="h-3.5 w-3.5" /> API Token
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => { setEditingUser(null); setShowUserModal(true); }} data-testid="create-user-btn">
            <UserPlus className="h-3.5 w-3.5" /> Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: 'Active', value: counts.active || 0, color: 'text-green-500' },
          { label: 'Inactive', value: counts.inactive || 0, color: 'text-amber-500' },
          { label: 'Deleted', value: counts.deleted || 0, color: 'text-red-500' },
          { label: 'API Users', value: counts.api_users || 0, color: 'text-orange-500' },
          { label: 'API Tokens', value: counts.api_tokens || 0, color: 'text-violet-500' },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs + Search */}
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="active" className="gap-1.5" data-testid="tab-users-active">
              <Users className="h-3.5 w-3.5" /> Active
            </TabsTrigger>
            <TabsTrigger value="inactive" className="gap-1.5" data-testid="tab-users-inactive">
              <EyeOff className="h-3.5 w-3.5" /> Inactive
            </TabsTrigger>
            <TabsTrigger value="deleted" className="gap-1.5" data-testid="tab-users-deleted">
              <Trash2 className="h-3.5 w-3.5" /> Deleted
            </TabsTrigger>
            <TabsTrigger value="api" className="gap-1.5" data-testid="tab-users-api">
              <Key className="h-3.5 w-3.5" /> API Users
            </TabsTrigger>
          </TabsList>
          <div className="relative w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search users..." className="pl-9 h-8 text-sm" data-testid="users-search" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <TabsContent value="active" className="mt-4 space-y-2">
              {/* Selection bar */}
              {tab === 'active' && filtered.length > 0 && (
                <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border">
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={selectedIds.size > 0 && selectedIds.size === filtered.filter(u => u.status === 'active').length}
                      onChange={selectAll} className="h-4 w-4 rounded border-slate-300 accent-indigo-500 cursor-pointer" data-testid="select-all-users" />
                    <span className="text-xs text-muted-foreground">
                      {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
                    </span>
                  </div>
                  {selectedIds.size > 0 && (
                    <Button size="sm" className="gap-1.5 h-7" onClick={handleSendSetupEmails} disabled={sendingEmails} data-testid="send-setup-emails-btn">
                      {sendingEmails ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      Send Setup Email ({selectedIds.size})
                    </Button>
                  )}
                </div>
              )}
              {filtered.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No active users found</p> :
                filtered.map(u => <UserRow key={u.id} user={u} onEdit={handleEdit} onDelete={handleDelete} onRestore={handleRestore} onToggleStatus={handleToggleStatus}
                  selected={selectedIds.has(u.id)} onToggleSelect={tab === 'active' ? toggleSelect : undefined} />)}
            </TabsContent>
            <TabsContent value="inactive" className="mt-4 space-y-2">
              {filtered.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No inactive users</p> :
                filtered.map(u => <UserRow key={u.id} user={u} onEdit={handleEdit} onDelete={handleDelete} onRestore={handleRestore} onToggleStatus={handleToggleStatus} />)}
            </TabsContent>
            <TabsContent value="deleted" className="mt-4 space-y-2">
              {filtered.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No deleted users</p> :
                filtered.map(u => <UserRow key={u.id} user={u} onEdit={handleEdit} onDelete={handleDelete} onRestore={handleRestore} onToggleStatus={handleToggleStatus} />)}
            </TabsContent>
            <TabsContent value="api" className="mt-4 space-y-4">
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">API Users</h3>
                {filtered.length === 0 ? <p className="text-sm text-muted-foreground py-4">No API users</p> :
                  filtered.map(u => <UserRow key={u.id} user={u} onEdit={handleEdit} onDelete={handleDelete} onRestore={handleRestore} onToggleStatus={handleToggleStatus} />)}
              </div>
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">API Tokens</h3>
                {tokens.length === 0 ? <p className="text-sm text-muted-foreground py-4">No API tokens created</p> :
                  tokens.map(t => <TokenRow key={t.token_id} token={t} onRevoke={handleRevokeToken} />)}
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Modals */}
      <UserModal isOpen={showUserModal} onClose={() => setShowUserModal(false)} user={editingUser} onSaved={loadData} />
      <ImportModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} onImported={loadData} />
      <ATSUserImportModal isOpen={showATSImportModal} onClose={() => setShowATSImportModal(false)} onImported={loadData} />
      <APITokenModal isOpen={showTokenModal} onClose={() => setShowTokenModal(false)} users={users} onCreated={loadData} />
    </div>
  );
}
